import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SingleSelectFilter } from '@/components/ui/single-select-filter';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, api } from '@/lib/api';
import { variantForTag } from '@/lib/tag-color';
import { extractVariableKeys } from '@/lib/template-vars';
import type {
  LlmEndpoint,
  ResponsePromptDetail,
  ResponsePromptTreeNode,
  ResponsePromptVariant,
  RunResponsePromptTestResponse,
} from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';

export interface PlaygroundPaneApi {
  run: () => Promise<void>;
  cancel: () => void;
  isRunning: () => boolean;
}

interface FlatFileOption {
  id: string;
  path: string; // "폴더1 / 폴더2 / 파일"
}

function flattenFiles(
  tree: ResponsePromptTreeNode[],
  parentPath: string[] = [],
  acc: FlatFileOption[] = [],
): FlatFileOption[] {
  for (const n of tree) {
    const path = [...parentPath, n.name];
    if (n.type === 'file') acc.push({ id: n.id, path: path.join(' / ') });
    if (n.children.length) flattenFiles(n.children, path, acc);
  }
  return acc;
}

interface Props {
  paneKey: 'left' | 'right';
  apiRef?: Ref<PlaygroundPaneApi>;
}

export function PlaygroundPane({ paneKey, apiRef }: Props) {
  const treeQ = useQuery({
    queryKey: ['admin', 'response-prompts', 'tree'],
    queryFn: () => api.responsePromptsTree(),
    staleTime: 30_000,
  });
  const endpointsQ = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
    staleTime: 60_000,
  });

  const fileOptions = useMemo(
    () => (treeQ.data ? flattenFiles(treeQ.data.tree) : []),
    [treeQ.data],
  );

  const [fileId, setFileId] = useState<string>('');
  const detailQ = useQuery({
    queryKey: ['admin', 'response-prompts', 'detail', fileId],
    queryFn: () => api.responsePromptDetail(fileId),
    enabled: fileId.length > 0,
  });
  const detail: ResponsePromptDetail | undefined = detailQ.data;

  /**
   * variant 선택 모드:
   *  - 'auto' : endpoint.model 로 자동 매칭 (정확 일치 → 기본 fallback)
   *  - uuid   : 그 variant 강제
   */
  const [variantMode, setVariantMode] = useState<'auto' | string>('auto');

  const defaultVariant = useMemo(
    () => detail?.variants.find((v) => v.modelMatch === '') ?? null,
    [detail],
  );

  // 버전 선택: '' = 현재 활성, 또는 특정 version id
  const [versionId, setVersionId] = useState<string>('');

  const [endpointId, setEndpointId] = useState<string>('');
  const [temperatureInput, setTemperatureInput] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RunResponsePromptTestResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const endpoints: LlmEndpoint[] = endpointsQ.data?.endpoints ?? [];
  const selectedEndpoint = endpoints.find((ep) => ep.id === endpointId) ?? null;

  /** variantMode + endpoint 로 결정된 실제 variant. */
  const activeVariant: ResponsePromptVariant | null = useMemo(() => {
    if (!detail) return null;
    if (variantMode === 'auto') {
      const m = selectedEndpoint?.model;
      if (m) {
        const exact = detail.variants.find((v) => v.modelMatch === m);
        if (exact) return exact;
      }
      return defaultVariant;
    }
    return detail.variants.find((v) => v.id === variantMode) ?? defaultVariant;
  }, [detail, variantMode, selectedEndpoint, defaultVariant]);

  const variantVersions = useMemo(
    () => detail?.versions.filter((v) => v.variantId === activeVariant?.id) ?? [],
    [detail, activeVariant],
  );

  const selectedContent = useMemo(() => {
    if (!activeVariant) return '';
    if (versionId === '') return activeVariant.currentContent ?? '';
    const v = variantVersions.find((vv) => vv.id === versionId);
    return v?.content ?? '';
  }, [activeVariant, versionId, variantVersions]);

  const selectedVariables = useMemo(() => {
    if (!activeVariant) return [];
    if (versionId === '') return activeVariant.currentVariables;
    const v = variantVersions.find((vv) => vv.id === versionId);
    return v?.variables ?? [];
  }, [activeVariant, versionId, variantVersions]);

  const variableKeys = useMemo(() => extractVariableKeys(selectedContent), [selectedContent]);

  // 파일/variant/버전 바뀔 때 변수 값 시드.
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    const obj: Record<string, string> = {};
    for (const v of selectedVariables) obj[v.key] = v.defaultValue;
    setValues(obj);
  }, [selectedVariables]);

  // variantMode / versionId 가 다른 variant 가리키면 버전 선택 reset (잘못된 버전 표시 방지).
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeVariant.id 만 트리거.
  useEffect(() => {
    setVersionId('');
  }, [activeVariant?.id]);

  const parsedTemperature = (() => {
    const s = temperatureInput.trim();
    if (s === '') return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  })();
  const temperatureInvalid =
    temperatureInput.trim() !== '' &&
    (parsedTemperature === undefined || parsedTemperature < 0 || parsedTemperature > 2);

  const run = async () => {
    if (!selectedContent.trim() || loading || temperatureInvalid) return;
    setLoading(true);
    setError(null);
    setResult(null);
    const ctl = new AbortController();
    abortRef.current = ctl;
    try {
      const r = await api.responsePromptRunTest(
        {
          content: selectedContent,
          variableValues: values,
          endpointId: endpointId || undefined,
          ...(activeVariant ? { variantId: activeVariant.id } : {}),
          ...(parsedTemperature !== undefined && !temperatureInvalid
            ? { temperature: parsedTemperature }
            : {}),
        },
        ctl.signal,
      );
      setResult(r);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // 조용히
      } else {
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : '실행 실패');
      }
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  const cancel = () => abortRef.current?.abort();

  // run/cancel 은 매 렌더 새로 생성되지만, 호출 시점의 최신 클로저를 ref 로 우회.
  const runRef = useRef(run);
  const cancelRef = useRef(cancel);
  runRef.current = run;
  cancelRef.current = cancel;
  useImperativeHandle(
    apiRef,
    () => ({
      run: () => runRef.current(),
      cancel: () => cancelRef.current(),
      isRunning: () => loading,
    }),
    [loading],
  );

  const selectedVersion = variantVersions.find((v) => v.id === versionId);
  const activeVersion = variantVersions.find((v) => v.version === activeVariant?.currentVersion);

  /** variant select 옵션 — "자동 매칭" + 각 variant. */
  const variantOptions = useMemo(() => {
    if (!detail) return [];
    const opts: Array<{ id: string; label: string; hint?: string }> = [
      { id: 'auto', label: '자동 매칭', hint: 'endpoint.model 기준' },
    ];
    const sorted = [...detail.variants].sort((a, b) => {
      if (a.modelMatch === '' && b.modelMatch !== '') return -1;
      if (a.modelMatch !== '' && b.modelMatch === '') return 1;
      return a.modelMatch.localeCompare(b.modelMatch);
    });
    for (const v of sorted) {
      opts.push({
        id: v.id,
        label: v.label?.trim() || (v.modelMatch === '' ? '기본' : v.modelMatch),
        hint: v.modelMatch === '' ? 'default' : v.modelMatch,
      });
    }
    return opts;
  }, [detail]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-border bg-surface-muted/30 px-3 py-2 text-xs font-medium text-foreground-muted">
        패널 {paneKey === 'left' ? 'A' : 'B'}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {/* 파일 선택 */}
        <div>
          <div className="block text-xs text-foreground-muted mb-1">프롬프트</div>
          <SingleSelectFilter
            options={fileOptions.map((f) => ({ id: f.id, label: f.path }))}
            value={fileId || null}
            onChange={(id) => {
              setFileId(id ?? '');
              setVersionId('');
              setVariantMode('auto');
              setResult(null);
              setError(null);
            }}
            triggerPlaceholder="— 선택하세요 —"
            searchPlaceholder="프롬프트 검색…"
          />
        </div>

        {/* Variant 선택 — 자동 매칭 또는 강제 */}
        {detail ? (
          <div>
            <div className="block text-xs text-foreground-muted mb-1">Variant</div>
            <SingleSelectFilter
              options={variantOptions}
              value={variantMode}
              onChange={(id) => setVariantMode((id as 'auto' | string) ?? 'auto')}
              triggerPlaceholder="자동 매칭"
              searchable={variantOptions.length > 6}
              searchPlaceholder="variant 검색…"
              clearable={false}
            />
            {variantMode === 'auto' && activeVariant ? (
              <div className="mt-1 text-[10px] text-foreground-muted">
                →{' '}
                {selectedEndpoint
                  ? activeVariant.modelMatch === '' && selectedEndpoint.model
                    ? `${selectedEndpoint.model} 매칭 없음 → 기본`
                    : activeVariant.modelMatch === ''
                      ? '기본 variant'
                      : `${activeVariant.modelMatch} variant 매칭`
                  : '기본 엔드포인트 + 기본 variant'}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* 버전 선택 */}
        {detail && activeVariant ? (
          <div>
            <div className="block text-xs text-foreground-muted mb-1">버전</div>
            <SingleSelectFilter
              options={[...variantVersions]
                .sort((a, b) => b.version - a.version)
                .map((v) => ({
                  id: v.id,
                  label: `v${v.version}${v.source === 'rollback' ? ' (롤백)' : ''}`,
                  hint: v.tags.length ? v.tags.join(', ') : undefined,
                }))}
              value={versionId || null}
              onChange={(id) => setVersionId(id ?? '')}
              triggerPlaceholder={
                activeVariant.currentVersion
                  ? `현재 활성 (v${activeVariant.currentVersion})`
                  : '활성 버전 없음'
              }
              searchable={variantVersions.length > 8}
              searchPlaceholder="버전 검색…"
            />
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {(selectedVersion ?? activeVersion)?.tags.map((t) => (
                <Badge key={t} variant={variantForTag(t)}>
                  {t}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}

        {/* 본문 (read-only) */}
        {selectedContent ? (
          <div>
            <div className="text-xs text-foreground-muted mb-1">본문 (read-only)</div>
            <Textarea
              readOnly
              value={selectedContent}
              rows={8}
              className="text-xs bg-surface-muted/30"
            />
          </div>
        ) : null}

        {/* 변수 값 */}
        {variableKeys.length > 0 ? (
          <div>
            <div className="text-xs text-foreground-muted mb-1">변수 값</div>
            <div className="space-y-2">
              {variableKeys.map((k) => {
                // 선택된 버전의 variable 정의에서 type/enumOptions 찾기.
                const def = selectedVariables.find((v) => v.key === k);
                const type = def?.type ?? 'string';
                const enumOptions = def?.enumOptions ?? [];
                return (
                  <div key={k}>
                    <label
                      className="block text-[10px] text-foreground-muted mb-0.5"
                      htmlFor={`pg-${paneKey}-var-${k}`}
                    >
                      {`{{${k}}}`}
                    </label>
                    {type === 'enum' && enumOptions.length > 0 ? (
                      <select
                        id={`pg-${paneKey}-var-${k}`}
                        value={values[k] ?? ''}
                        onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                        className="h-8 w-full rounded-md border border-border bg-surface px-2 text-sm"
                      >
                        <option value="">— 선택 —</option>
                        {enumOptions.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        id={`pg-${paneKey}-var-${k}`}
                        type={type === 'number' ? 'number' : 'text'}
                        value={values[k] ?? ''}
                        onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                        className="h-8 text-sm"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* 엔드포인트 + Temperature + 실행/취소 */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <div className="block text-xs text-foreground-muted mb-1">LLM 엔드포인트</div>
            <SingleSelectFilter
              options={endpoints.map((ep) => ({ id: ep.id, label: ep.name, hint: ep.model }))}
              value={endpointId || null}
              onChange={(id) => setEndpointId(id ?? '')}
              triggerPlaceholder="기본 엔드포인트"
              searchPlaceholder="엔드포인트 검색…"
              searchable={endpoints.length > 6}
              triggerClassName="h-9 text-sm"
            />
          </div>
          <div className="w-24">
            <div className="block text-xs text-foreground-muted mb-1">Temp</div>
            <Input
              type="number"
              min={0}
              max={2}
              step={0.01}
              placeholder={
                activeVariant?.temperature != null ? `자동 ${activeVariant.temperature}` : '기본값'
              }
              value={temperatureInput}
              onChange={(e) => setTemperatureInput(e.target.value)}
              aria-invalid={temperatureInvalid}
              className="h-9 text-sm"
            />
          </div>
          {loading ? (
            <Button type="button" variant="secondary" onClick={cancel} className="h-9">
              취소
            </Button>
          ) : (
            <Button
              type="button"
              onClick={run}
              disabled={!selectedContent.trim() || temperatureInvalid}
              className="h-9"
            >
              실행
            </Button>
          )}
        </div>
        {temperatureInvalid ? (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
            Temperature 는 0 과 2 사이 숫자여야 합니다.
          </div>
        ) : null}

        {/* 결과 */}
        {loading ? (
          <div className="rounded-md bg-accent/5 px-3 py-2 text-xs text-accent">실행 중…</div>
        ) : null}
        {error ? (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        ) : null}
        {result ? (
          <div className="space-y-1">
            <div className="text-xs text-foreground-muted">
              {result.endpointName ? `${result.endpointName} · ` : ''}
              {result.model ?? ''}
              <span className="ml-1">· {result.durationMs}ms</span>
            </div>
            <Textarea readOnly value={result.output} rows={10} className="text-sm" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
